// <tri-scan-progress> — live progress for a queued/running scan. Markup
// convention (server renders the initial numbers; this element only keeps
// them fresh):
//
//   <tri-scan-progress data-progress-url="/scans/12/progress" data-interval="4000">
//     …
//     <span data-progress-count>34</span> of 141
//     <div … ><span data-progress-bar style="width: 24%"></span></div>
//     <span data-progress-detail>3 running · 104 pending</span>
//   </tri-scan-progress>
//
// Polls the JSON endpoint (ScansController#progress) on the interval and
// writes the payload into the tagged nodes. When the scan reaches a
// terminal status the whole page is revisited via Turbo (replace, so Back
// isn't polluted) to swap the progress card for the finished report.
// Without JS the server-rendered numbers stand — refresh to update.

const TERMINAL_STATUSES = new Set([
	"complete",
	"completed_with_errors",
	"failed",
]);

class TriScanProgress extends HTMLElement {
	#timer: number | null = null;

	connectedCallback() {
		const url = this.dataset.progressUrl;
		if (!url) return;

		const interval = Number(this.dataset.interval) || 4000;
		this.#timer = window.setInterval(() => this.#tick(url), interval);
	}

	disconnectedCallback() {
		if (this.#timer !== null) window.clearInterval(this.#timer);
		this.#timer = null;
	}

	async #tick(url: string) {
		let payload: {
			status: string;
			total: number;
			finished: number;
			running: number;
			pending: number;
			failed: number;
			percent: number;
		};
		try {
			const response = await fetch(url, {
				headers: { Accept: "application/json" },
			});
			if (!response.ok) return; // transient — try again next tick
			payload = await response.json();
		} catch {
			return;
		}

		if (TERMINAL_STATUSES.has(payload.status)) {
			this.disconnectedCallback();
			// biome-ignore lint/suspicious/noExplicitAny: Turbo global from turbo-rails
			const turbo = (window as any).Turbo;
			if (turbo) turbo.visit(location.href, { action: "replace" });
			else location.reload();
			return;
		}

		const count = this.querySelector<HTMLElement>("[data-progress-count]");
		if (count) count.textContent = String(payload.finished);

		const bar = this.querySelector<HTMLElement>("[data-progress-bar]");
		if (bar) bar.style.width = `${payload.percent}%`;

		const detail = this.querySelector<HTMLElement>("[data-progress-detail]");
		if (detail) {
			const parts = [
				payload.running > 0 ? `${payload.running} running` : null,
				payload.pending > 0 ? `${payload.pending} pending` : null,
				payload.failed > 0 ? `${payload.failed} failed` : null,
			].filter(Boolean);
			detail.textContent = parts.join(" · ");
		}
	}
}

if (!customElements.get("tri-scan-progress")) {
	customElements.define("tri-scan-progress", TriScanProgress);
}

export {};
